import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { faMailBulk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXTwitter, faGithub } from "@fortawesome/free-brands-svg-icons";

import Footer from "../components/common/footer";
import NavBar from "../components/common/navBar";
import Article from "../components/homepage/article";
import Works from "../components/homepage/works";
import AllProjects from "../components/projects/allProjects";

import SEO from "../data/seo";
import { useTheme } from "../contexts/ThemeContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { useArticles } from "../hooks/useArticles";
import { Theme } from "../theme";

// アニメーション定義
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	position: relative;
	overflow-x: hidden;
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 0 ${props => props.theme.density.pageGutter};
	position: relative;
	z-index: 10;

	@media (max-width: 768px) {
		padding: 0 ${props => props.theme.density.pageGutterMobile};
	}
`;

const HeroSection = styled.div`
	padding: 1.25rem 0 0.75rem;
	animation: ${fadeInUp} 0.3s ease-out 0.1s both;
`;

const HeroTop = styled.div`
	display: flex;
	align-items: center;
	gap: 1.25rem;

	@media (max-width: 768px) {
		gap: 1.5rem;
		margin-bottom: 0.75rem;
	}
`;

const HeroContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.display.fontSize};
	font-weight: ${props => props.theme.typography.roles.display.fontWeight};
	letter-spacing: ${props => props.theme.typography.roles.display.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	line-height: ${props => props.theme.typography.roles.display.lineHeight};
	margin: 0;
	animation: ${fadeInUp} 0.3s ease-out 0.1s both;

	@media (max-width: 768px) {
		letter-spacing: -0.025em;
	}
`;

const Hurigana = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.label.fontSize};
	font-weight: ${props => props.theme.typography.roles.label.fontWeight};
	color: ${props => props.theme.colors.text.secondary};
	margin: 0;
	animation: ${fadeInUp} 0.3s ease-out 0.15s both;

	@media (max-width: 768px) {
		font-size: 0.9rem;
	}
`;

const Description = styled.p<{ theme?: Theme }>`
	font-size: ${props => props.theme?.typography.roles.body.fontSize};
	line-height: ${props => props.theme?.typography.roles.body.lineHeight};
	color: ${props => props.theme?.colors.text.secondary || '#27272a'};
	margin: 0;
	white-space: pre-line;
	animation: ${fadeInUp} 0.3s ease-out 0.2s both;

	@media (max-width: 768px) {
		font-size: 0.95rem;
		line-height: 1.6;
	}
`;

const HeroImageContainer = styled.div`
	flex-shrink: 0;
`;

const HeroImage = styled.div`
	width: 128px;
	height: 128px;
	border-radius: 1.5rem;
	overflow: hidden;
	box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	&:hover {
		box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
		transform: scale(1.05);
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	@media (max-width: 1024px) {
		width: 112px;
		height: 112px;
		border-radius: 1.25rem;
	}

	@media (max-width: 768px) {
		width: 80px;
		height: 80px;
		border-radius: 1rem;
	}
`;

const SocialsContainer = styled.div`
	display: flex;
	gap: 0.5rem;
	padding-top: 0.75rem;
	animation: ${fadeInUp} 0.3s ease-out 0.25s both;
`;

const SocialIcon = styled.a<{ theme?: Theme }>`
	font-size: 1.125rem;
	color: ${props => props.theme?.colors.text.secondary || '#52525b'};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.75rem;
	height: 2.75rem;
	border-radius: 50%;
	background: ${props => props.theme?.colors.background.secondary || '#f4f4f5'};
	border: 1px solid ${props => props.theme?.colors.neutral[200] || '#e4e4e7'};

	&:hover {
		transform: translateY(-4px) scale(1.1);
		background: ${props => props.theme?.mode === 'light' ? '#f0f4ff' : props.theme?.colors.neutral[100]};
		color: ${props => props.theme?.colors.primary.main || '#667eea'};
		box-shadow: 0 10px 30px rgba(102, 126, 234, 0.15);
	}
`;

const Section = styled.section<{ theme?: Theme }>`
	margin: ${props => props.theme?.density.sectionGap ?? '2rem'} 0;
	animation: ${fadeInUp} 0.3s ease-out 0.3s both;
`;

const ProjectsSection = styled(Section)<{ theme?: Theme }>`
`;

const ArticlesWorksContainer = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(240px, 1fr);
	gap: 1rem;
	margin: 2rem 0;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
		gap: 1rem;
	}
`;

const ArticlesContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const WorksContainer = styled.div`
	/* Card component already has all necessary styles */
`;

const FooterContainer = styled.div<{ theme?: Theme }>`
	margin-top: 2.5rem;
	padding: 0.75rem 0;
`;

const Homepage = () => {
	const { theme } = useTheme();
	const userInfo = useUserInfo();
	const articles = useArticles();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "home")!;

	return (
		<React.Fragment>
			<Helmet>
				<title>{userInfo.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="home" />
				<ContentWrapper theme={theme}>
					<HeroSection>
						<HeroTop>
							<HeroImageContainer>
								<HeroImage>
									<img src="homepage.jpg" alt="Profile" />
								</HeroImage>
							</HeroImageContainer>

							<HeroContent>
								<Title theme={theme}>{userInfo.homepage.name}</Title>
								<Hurigana theme={theme}>{userInfo.homepage.hurigana}</Hurigana>
							</HeroContent>
						</HeroTop>
						<Description theme={theme}>{userInfo.homepage.description}</Description>
						<SocialsContainer>
							<SocialIcon theme={theme} href={userInfo.socials.twitter} target="_blank" rel="noreferrer" aria-label="X (Twitter)">
								<FontAwesomeIcon icon={faXTwitter} />
							</SocialIcon>
							<SocialIcon theme={theme} href={userInfo.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub">
								<FontAwesomeIcon icon={faGithub} />
							</SocialIcon>
							<SocialIcon theme={theme} href={`mailto:${userInfo.main.email}`} aria-label="Email">
								<FontAwesomeIcon icon={faMailBulk} />
							</SocialIcon>
						</SocialsContainer>
					</HeroSection>

					<ProjectsSection theme={theme}>
						<AllProjects />
					</ProjectsSection>

					<ArticlesWorksContainer>
						<ArticlesContainer>
							{articles.map((article, index) => (
								<div key={(index + 1).toString()}>
									<Article
										date={article.date}
										title={article.title}
										description={article.description}
										link={"/article/" + (index + 1)}
									/>
								</div>
							))}
						</ArticlesContainer>

					<WorksContainer>
						<Works />
					</WorksContainer>
					</ArticlesWorksContainer>

					<FooterContainer theme={theme}>
						<Footer />
					</FooterContainer>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default Homepage;
